import React from 'react'
import { CircularProgress } from '@material-ui/core'

// @src imports
import { DefaultError, Icon, SuspenseBoundary, Transition } from 'src/chrome'
import SOCImages from 'src/editor/images/SOCImages'
import FacebookImages from 'src/editor/components/FacebookImages/FacebookImages'
import Layouts from 'src/editor/components/Layouts/Layouts'
// relative imports
import BackgroundPicker from '../../components/BackgroundPicker/BackgroundPicker'
import MinimalTextOptions from '../../components/MinimalTextOptions/MinimalTextOptions'
import MinimalImageOptions from '../../components/MinimalImageOptions'
import TextControls from '../../components/TextControls/TextControls'
import { Api } from '../../api'
import { Step, Steps } from '../../types'

import styles from '../../styles/components/Drawer.module.scss'
import { useCallback, useSelector, useState } from 'src/hooks'
import CloseButton from 'src/chrome/CloseButton/CloseButton'

const hideToolBar: React.CSSProperties = {
  bottom: 0,
  zIndex: 1000,
}

const drawerTriggerText = (step: Step) => {
  switch (step.type) {
    case Steps.EditPicture:
    case Steps.EditBackgroundPicture:
      switch (step.source) {
        case 'own':
          return 'Use your own photos'
        case 'social':
          return 'Use your Facebook photos'
      }
      break
    case Steps.EditLayout:
      return 'Pick a Layout'
    case Steps.EditText:
      return 'Add your Message'
    case Steps.EditBackgroundColor:
      return 'Spice up your panel'
    case Steps.Idle:
      return ''
  }
}

const DrawerContent: React.FC<{
  api: Api
  isMobile: boolean
}> = ({ api, isMobile }) => {
  const { step } = api

  const [shouldUseRegularTransition, setShouldUseRegularTransition] = useState<
    boolean
  >(false)

  const onChangeTransitionType = useCallback(
    (shouldUseRegularTransition: boolean) => {
      setShouldUseRegularTransition(shouldUseRegularTransition)
    },
    [setShouldUseRegularTransition],
  )

  switch (step.type) {
    case Steps.EditPicture:
    case Steps.EditBackgroundPicture:
      switch (step.source) {
        case 'own':
          return isMobile ? (
            <MinimalImageOptions api={{ ...api, step }} />
          ) : (
            <SOCImages api={{ ...api, step }} />
          )
        case 'social':
          return isMobile ? (
            <MinimalImageOptions api={{ ...api, step }} />
          ) : (
            <FacebookImages api={{ ...api, step }} />
          )
      }
      break
    case Steps.EditLayout:
      return (
        <SuspenseBoundary
          unresolved={
            shouldUseRegularTransition ? (
              <Transition message="Updating your layout..." />
            ) : (
              <div className={styles.Loader}>
                <CircularProgress />
              </div>
            )
          }
          failure={DefaultError}
        >
          <Layouts
            api={{ ...api, step }}
            onChangeTransitionType={onChangeTransitionType}
          />
        </SuspenseBoundary>
      )
    case Steps.EditBackgroundColor:
      return <BackgroundPicker api={{ ...api, step }} />
    case Steps.EditText:
      return isMobile ? (
        <MinimalTextOptions api={{ ...api, step }} />
      ) : (
        <TextControls api={{ ...api, step }} />
      )
    case Steps.Idle:
      return null
  }
}

interface Props {
  api: Api
}

const EditorDrawer: React.FC<Props> = props => {
  const { api } = props
  const { step, setStep, resetStep } = api

  const { isMobile } = useSelector(state => state.window)

  return (
    <section className="openDrawer">
      <div
        className={`${styles.imageDrawer}`}
        style={
          step.type === Steps.EditPicture || step.type === Steps.EditText
            ? hideToolBar
            : {}
        }
      >
        {isMobile && (
          <span
            style={{
              height: 28,
            }}
            className={styles.drawerClose}
            onClick={() => resetStep()}
          >
            <Icon
              icon={'CARROTDOWN'}
              color={'#6f8394'}
              size={32}
              viewBox={'0 0 32 32'}
            />
          </span>
        )}
        {step.type !== Steps.EditText && !isMobile && (
          <div className={styles.drawerBarTop}>
            {(step.type === Steps.EditPicture ||
              step.type === Steps.EditBackgroundPicture) && (
              <nav className={styles.imagePanelNav}>
                <li
                  className={
                    step.type === Steps.EditPicture &&
                    step.source === 'own' &&
                    styles.selected
                  }
                  onClick={() => setStep({ ...step, source: 'own' })}
                >
                  My Images
                </li>
                <li
                  className={step.source === 'social' ? styles.selected : ''}
                  onClick={() => setStep({ ...step, source: 'social' })}
                >
                  Social Media
                </li>
              </nav>
            )}
            <span className={styles.drawerTitle}>
              <span>{drawerTriggerText(step)}</span>
              <CloseButton
                size={24}
                onClose={() => resetStep()}
                id="close_editor_drawer"
              />
            </span>
          </div>
        )}
        <DrawerContent api={api} isMobile={isMobile} />
      </div>
    </section>
  )
}

export default EditorDrawer
